import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useProfile } from '@/contexts/ProfileContext';
import { fetchProfilesByIds } from '@/lib/agreements';
import {
  fetchChatReports,
  fetchReportedMessageBody,
  markChatReportReviewed,
  type ChatReportRow,
} from '@/lib/chatModeration';
import type { Profile } from '@/lib/database.types';

type FilterKey = 'pending' | 'reviewed' | 'all';

export default function AdminChatReportsScreen() {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const isAdmin = !!profile?.is_admin;

  const [rows, setRows] = useState<ChatReportRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('pending');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [messageBodies, setMessageBodies] = useState<Record<string, string | null>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const data = await fetchChatReports(filter);
      setRows(data);
      const userIds = [...new Set(data.flatMap((r) => [r.reporter_id, r.reported_user_id]))];
      if (userIds.length > 0) setProfiles(await fetchProfilesByIds(userIds));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, isAdmin]);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  if (!isAdmin) {
    return (
      <View style={[styles.center, { paddingBottom: insets.bottom }]}>
        <Text style={styles.errorText}>{t('adminScreen.noAccessScreen')}</Text>
      </View>
    );
  }

  async function onExpand(row: ChatReportRow) {
    const opening = expandedId !== row.id;
    setExpandedId(opening ? row.id : null);
    if (opening && row.message_id && !(row.message_id in messageBodies)) {
      const body = await fetchReportedMessageBody(row.message_id).catch(() => null);
      setMessageBodies((prev) => ({ ...prev, [row.message_id!]: body }));
    }
  }

  async function onMarkReviewed(row: ChatReportRow) {
    setBusyId(row.id);
    try {
      await markChatReportReviewed(row.id);
      if (filter === 'pending') {
        setRows((prev) => prev.filter((r) => r.id !== row.id));
      } else {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, reviewed: true } : r)));
      }
    } catch {
      appAlert(t('common.error'), t('adminScreen.chatReportsActionFailed'));
    } finally {
      setBusyId(null);
    }
  }

  const FILTERS: { key: FilterKey; label: string }[] = [
    { key: 'pending', label: t('adminScreen.chatReportsFilter_pending') },
    { key: 'reviewed', label: t('adminScreen.chatReportsFilter_reviewed') },
    { key: 'all', label: t('adminScreen.feedbackFilter_all') },
  ];

  return (
    <View style={styles.flex}>
      <View style={[styles.topBar, { paddingTop: insets.top + spacing.md }]}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('adminScreen.chatReportsTitle')}</Text>
        <View style={styles.spacer} />
      </View>

      <View style={styles.tabs}>
        {FILTERS.map((f) => (
          <Pressable
            key={f.key}
            style={[styles.tab, filter === f.key && styles.tabActive]}
            onPress={() => setFilter(f.key)}
            accessibilityRole="tab"
          >
            <Text style={[styles.tabText, filter === f.key && styles.tabTextActive]}>{f.label}</Text>
          </Pressable>
        ))}
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : rows.length === 0 ? (
        <View style={styles.center}>
          <Ionicons name="flag-outline" size={40} color={colors.border} />
          <Text style={styles.emptyText}>{t('adminScreen.chatReportsEmpty')}</Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={[styles.list, { paddingBottom: insets.bottom + spacing.xl }]}>
          {rows.map((row) => {
            const reporter = profiles[row.reporter_id];
            const reported = profiles[row.reported_user_id];
            const expanded = expandedId === row.id;
            const busy = busyId === row.id;
            return (
              <Pressable key={row.id} style={styles.card} onPress={() => onExpand(row)}>
                <View style={styles.cardTop}>
                  <View style={styles.cardTopLeft}>
                    {!row.reviewed && <View style={styles.newDot} />}
                    <Text style={styles.cardReason}>{t(`agreementDetail.reportReason_${row.reason}`)}</Text>
                  </View>
                  <Text style={styles.cardDate}>{new Date(row.created_at).toLocaleDateString('el-GR')}</Text>
                </View>
                <Text style={styles.cardUsers}>
                  {t('adminScreen.chatReportsUsersLine', {
                    reporter: reporter?.full_name?.trim() || t('adminScreen.feedbackUnknownUser'),
                    reported: reported?.full_name?.trim() || t('adminScreen.feedbackUnknownUser'),
                  })}
                </Text>
                {!!row.note && <Text style={styles.cardNote} numberOfLines={expanded ? undefined : 2}>{row.note}</Text>}
                {expanded && (
                  <>
                    {!!row.message_id && (
                      <View style={styles.messageBox}>
                        <Text style={styles.messageBoxLabel}>{t('adminScreen.chatReportsReportedMessage')}</Text>
                        <Text style={styles.messageBoxText}>
                          {messageBodies[row.message_id] === undefined
                            ? '…'
                            : messageBodies[row.message_id] ?? t('adminScreen.chatReportsMessageDeleted')}
                        </Text>
                      </View>
                    )}
                    <Pressable
                      style={styles.viewAgreementLink}
                      onPress={() => router.push({ pathname: '/(app)/agreements/[id]', params: { id: row.agreement_id } })}
                      accessibilityRole="button"
                    >
                      <Text style={styles.viewAgreementLinkText}>{t('adminScreen.chatReportsViewAgreement')}</Text>
                    </Pressable>
                    {!row.reviewed && (
                      <View style={styles.actionRow}>
                        <Pressable style={[styles.actionBtn, styles.actionBtnPrimary]} onPress={() => onMarkReviewed(row)} disabled={busy}>
                          <Text style={styles.actionBtnPrimaryText}>{busy ? '…' : t('adminScreen.chatReportsMarkReviewed')}</Text>
                        </Pressable>
                      </View>
                    )}
                  </>
                )}
              </Pressable>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.md,
      paddingHorizontal: spacing.xl,
    },
    errorText: { fontSize: fontSize.md, color: colors.danger, textAlign: 'center' },
    emptyText: { fontSize: fontSize.md, color: colors.textMuted, textAlign: 'center' },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.md,
    },
    back: { fontSize: fontSize.md, fontWeight: '600', color: colors.primary },
    title: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    spacer: { width: 44 },
    tabs: {
      flexDirection: 'row',
      marginHorizontal: spacing.lg,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: 'hidden',
      marginBottom: spacing.sm,
    },
    tab: { flex: 1, paddingVertical: 10, alignItems: 'center' },
    tabActive: { backgroundColor: colors.primary },
    tabText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    tabTextActive: { color: colors.textOnPrimary },
    list: { paddingHorizontal: spacing.lg, gap: spacing.sm },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.xs,
    },
    cardTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    cardTopLeft: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 },
    newDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.danger },
    cardReason: { color: colors.text, fontSize: fontSize.sm, fontWeight: '700', flexShrink: 1 },
    cardDate: { color: colors.textMuted, fontSize: fontSize.xs },
    cardUsers: { color: colors.textMuted, fontSize: fontSize.xs },
    cardNote: { color: colors.text, fontSize: fontSize.sm, lineHeight: 20 },
    messageBox: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.sm,
      marginTop: spacing.xs,
    },
    messageBoxLabel: { color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '700', marginBottom: 2 },
    messageBoxText: { color: colors.text, fontSize: fontSize.sm, lineHeight: 19 },
    viewAgreementLink: { marginTop: spacing.xs },
    viewAgreementLinkText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    actionRow: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.xs },
    actionBtn: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingVertical: 10,
      alignItems: 'center',
    },
    actionBtnPrimary: { backgroundColor: colors.primary, borderColor: colors.primary },
    actionBtnPrimaryText: { color: colors.textOnPrimary, fontSize: fontSize.sm, fontWeight: '700' },
  });
}
